import { useState } from "react";
import { REGIONS, SORTS, YEARS } from "@/mocks/site";

interface FilterBarProps {
  region: string;
  year: string;
  sort: string;
  count: number;
  onChange: (patch: { region?: string; year?: string; sort?: string }) => void;
  onReset: () => void;
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[] | { key: string; label: string }[];
  onChange: (value: string) => void;
}) {
  const normalized = options.map((option) =>
    typeof option === "string" ? { key: option, label: option } : option,
  );
  return (
    <label className="flex min-w-0 flex-1 flex-col gap-1.5">
      <span className="font-label text-[11px] font-medium uppercase tracking-[0.12em] text-foreground-400">
        {label}
      </span>
      <div className="relative">
        <select
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="h-10 w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 pl-3 pr-8 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
        >
          {normalized.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-2.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
          <i className="ri-arrow-down-s-line text-base" />
        </span>
      </div>
    </label>
  );
}

export default function FilterBar({ region, year, sort, count, onChange, onReset }: FilterBarProps) {
  const [open, setOpen] = useState(false);
  const dirty = region !== REGIONS[0] || year !== YEARS[0] || sort !== SORTS[0].key;

  return (
    <div className="rounded-lg border border-background-200 bg-background-100/60 p-3 md:p-4">
      <div className="flex items-center justify-between gap-3 lg:hidden">
        <div className="flex items-center gap-2 text-sm text-foreground-600">
          <i className="ri-filter-3-line text-base text-foreground-500" />
           <span>
            筛选 · <span className="font-semibold text-foreground-900">{count}</span> 部结果
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-700"
        >
          {open ? "收起" : "展开"}
          <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
        </button>
      </div>

      <div className={`${open ? "mt-3 grid" : "hidden"} grid-cols-1 gap-3 sm:grid-cols-3 lg:mt-0 lg:grid lg:grid-cols-12 lg:items-end lg:gap-3`}>
        <div className="lg:col-span-3">
          <SelectField
            label="地区"
            value={region}
            options={REGIONS}
            onChange={(value) => onChange({ region: value })}
          />
        </div>
        <div className="lg:col-span-3">
          <SelectField label="年份" value={year} options={YEARS} onChange={(value) => onChange({ year: value })} />
        </div>
        <div className="lg:col-span-3">
          <SelectField label="排序" value={sort} options={SORTS} onChange={(value) => onChange({ sort: value })} />
        </div>
        <div className="flex items-center justify-between gap-2 lg:col-span-3 lg:justify-end">
          <span className="hidden text-xs text-foreground-500 lg:inline">
            共 <span className="font-semibold text-foreground-900">{count}</span> 部
          </span>
          <button
            type="button"
            onClick={onReset}
            disabled={!dirty}
            className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}